import { ErrorCard } from "@fero/components";
import {
  useGetNominatedPurchaserOutstandingBalanceSummaryPdfQuery,
  useGetNominatedPurchasersForOrgQuery,
} from "@fero/hooks";
import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { usePurchaserSalePriceStore, useTradeSalesStore } from "@fero/stores";
import { AppRoutes } from "@fero/utils";
import Arrow from "@mui/icons-material/Straight";
import { Button, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import type { NominatedPurchaserDetailsView } from "adl-gen/ferovinum/app/api";
import type { AttachedDocument } from "adl-gen/ferovinum/app/uploads";
import { DocumentDownloader } from "components/widgets/buttons/document-downloader";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import type { PurchaseRequestFlowState } from "../purchase-request-types";
import {
  PurchaserCreditCard,
  PurchaserDiscountsCard,
  SavedDeliveryAddressCard,
} from "./components";

const PurchaserDetailsSkeleton = () => (
  <PortalPageContent>
    <Stack spacing={2}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Skeleton variant="text" height={80} width={320} animation="wave" />
        <Skeleton variant="text" height={80} width={180} animation="wave" />
      </Stack>
      <Skeleton variant="rounded" height={250} animation="wave" />
      <Skeleton variant="rounded" height={80} animation="wave" />
      <Skeleton variant="rounded" height={130} animation="wave" />
    </Stack>
  </PortalPageContent>
);

const PurchaserDetailsHeader: React.FC<{
  nominatedPurchaser: NominatedPurchaserDetailsView;
}> = ({ nominatedPurchaser }) => {
  const history = useHistory();

  return (
    <Stack direction={"row"} alignItems={"center"}>
      <IconButton
        onClick={() => {
          history.push(AppRoutes.OrganisationPurchaseRequestPurchasers);
        }}
      >
        <Arrow sx={{ transform: "rotate(-90deg)" }} />
      </IconButton>
      <Typography variant="h4">{nominatedPurchaser.name}</Typography>
    </Stack>
  );
};

export const PurchaserDetailsPage = () => {
  const history = useHistory();
  const { nominatedPurchaserId } = useParams<{
    nominatedPurchaserId: string;
  }>();
  const {
    setPurchaserId,
    setOrganisationId,
    setCurrency,
    setPurchaserName,
    setPurchaserDiscount,
  } = usePurchaserSalePriceStore();
  const {
    data: nominatedPurchasers = [],
    isLoading,
    isError,
  } = useGetNominatedPurchasersForOrgQuery();
  const { updateOptions, clearEditState } = useTradeSalesStore();
  const nominatedPurchaser = nominatedPurchasers.find(
    (np) => np.nominatedPurchaserId === nominatedPurchaserId,
  );
  // Whenever we change purchaser, update the store
  useEffect(() => {
    if (nominatedPurchaser) {
      setOrganisationId(nominatedPurchaser.organisationId);
      setPurchaserId(nominatedPurchaser.purchaserId);
      setCurrency(nominatedPurchaser.currency);
      setPurchaserName(nominatedPurchaser.name);
      setPurchaserDiscount(nominatedPurchaser.savedDiscountPct);
    }
  }, [
    nominatedPurchaser,
    setOrganisationId,
    setPurchaserId,
    setCurrency,
    setPurchaserName,
    setPurchaserDiscount,
  ]);

  if (isLoading) {
    return <PurchaserDetailsSkeleton />;
  }

  if (isError) {
    return (
      <PortalPageContent>
        <ErrorCard />
      </PortalPageContent>
    );
  }

  if (!nominatedPurchaser) {
    return (
      <PortalPageContent>
        <ErrorCard message="Could not find this purchaser" />
      </PortalPageContent>
    );
  }

  const onClickCreateOrder = () => {
    const salePriceType = "exDutyAndVat";
    const initialFlowState: PurchaseRequestFlowState = {
      setup: {
        purchaser: nominatedPurchaser,
        dealDiscountAmount: null,
        products: [],
        bondedSale: true,
        salePriceType,
      },
    };
    clearEditState();
    updateOptions({ isEditMode: false, salePriceType });
    history.push(AppRoutes.OrganisationCreatePurchaseRequest, initialFlowState);
  };

  return (
    <PortalPageContent
      header={
        <PortalPageContentHeader
          title={
            <PurchaserDetailsHeader nominatedPurchaser={nominatedPurchaser} />
          }
          variant="split"
          right={
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {nominatedPurchaser.outstandingBalanceSummaryEnabled && (
                <OutstandingBalanceSummaryDownloader
                  nominatedPurchaserId={nominatedPurchaserId}
                />
              )}
              <Button onClick={onClickCreateOrder}>Create Order</Button>
            </Stack>
          }
        />
      }
    >
      <Stack spacing={2}>
        <PurchaserDiscountsCard nominatedPurchaserId={nominatedPurchaserId} />
        <PurchaserCreditCard nominatedPurchaserId={nominatedPurchaserId} />
        <SavedDeliveryAddressCard nominatedPurchaserId={nominatedPurchaserId} />
      </Stack>
    </PortalPageContent>
  );
};

const OutstandingBalanceSummaryDownloader: React.FC<{
  nominatedPurchaserId: string;
}> = ({ nominatedPurchaserId }) => {
  const { mutate: fetchPdf } =
    useGetNominatedPurchaserOutstandingBalanceSummaryPdfQuery();
  // we are creating a fake document here because we don't persist this pdf
  const attachedDocuments: AttachedDocument[] = useMemo(
    () => [
      {
        documentType: "NominatedPurchaserOutstandingBalanceSummary",
        storedFileId: `NominatedPurchaserOutstandingBalanceSummaryFakeStore${nominatedPurchaserId}`,
        invoiceId: nominatedPurchaserId,
        page: 1,
      },
    ],
    [nominatedPurchaserId],
  );

  const handleOutstandingBalanceSummaryDownload = useCallback(
    async (doc: AttachedDocument) => {
      if (doc.documentType === "NominatedPurchaserOutstandingBalanceSummary") {
        fetchPdf(nominatedPurchaserId);
      }
    },
    [fetchPdf, nominatedPurchaserId],
  );

  return (
    <DocumentDownloader
      attachedDocuments={attachedDocuments}
      downloadDocument={handleOutstandingBalanceSummaryDownload}
    />
  );
};
